/**
 * Canonical WAIT-duration helpers, shared by validation, the node-card display,
 * and the header summary so all three agree on exactly one shape.
 *
 * The wait editor emits a single unit only — "Nd" | "Nh" | "Nm" — and the
 * backend ParseWaitDuration (graph.go) accepts the same. Parsing here matches
 * that single canonical form; anything else is treated as "unset" rather than
 * silently contributing 0 in one place and a real value in another.
 */

// MAX_WAIT_DAYS mirrors the backend MaxWaitDuration cap (90 days).
export const MAX_WAIT_DAYS = 90

type WaitUnit = 'd' | 'h' | 'm'

const UNIT_MINUTES: Record<WaitUnit, number> = { d: 1440, h: 60, m: 1 }

type ParsedWaitDuration = { value: number; unit: WaitUnit }

/** Parse the canonical "Nd" | "Nh" | "Nm" form. Returns undefined otherwise. */
export function parseWaitDuration(raw: string): ParsedWaitDuration | undefined {
  const match = raw.trim().toLowerCase().match(/^(\d+)(d|h|m)$/)
  if (!match) return undefined
  return { value: Number(match[1]), unit: match[2] as WaitUnit }
}

/** Total minutes for a wait duration; 0 for an unparseable/empty value. */
export function waitDurationMinutes(raw: string): number {
  const parsed = parseWaitDuration(raw)
  return parsed ? parsed.value * UNIT_MINUTES[parsed.unit] : 0
}

/** Fractional days for the 90-day cap check; undefined when unparseable. */
export function waitDurationDays(raw: string): number | undefined {
  const parsed = parseWaitDuration(raw)
  return parsed ? (parsed.value * UNIT_MINUTES[parsed.unit]) / UNIT_MINUTES.d : undefined
}

/** Long-form label, e.g. "3 days" / "2 hours" / "30 minutes". Passes through unknown shapes. */
export function humanizeWaitDuration(raw: string): string {
  const parsed = parseWaitDuration(raw)
  if (!parsed) return raw
  const noun = parsed.unit === 'd' ? 'day' : parsed.unit === 'h' ? 'hour' : 'minute'
  return `${parsed.value} ${noun}${parsed.value === 1 ? '' : 's'}`
}
